import { Badge, BadgeProps, Group, Text, Tooltip } from '@mantine/core';
import { IconAlertTriangle, IconCalendar } from '@tabler/icons-react';
import {
  BD_REVIEW_STATUS_LABELS,
  businessToday,
  REQUEST_STATUS_LABELS,
  WORK_ITEM_STATUS_LABELS,
  type BdReviewStatus,
  type RequestStatus,
  type WorkItemStatus,
} from '@mexp/shared';
import { formatBusinessDate } from '@/lib/format';

const REQUEST_COLORS: Record<RequestStatus, string> = { in_progress: 'blue', completed: 'teal', cancelled: 'gray' };
const BD_COLORS: Record<BdReviewStatus, string> = { not_sent: 'gray', pending_review: 'violet', returned: 'orange', approved: 'teal' };
const ITEM_COLORS: Record<WorkItemStatus, string> = { assigned: 'blue', submitted: 'yellow', returned: 'orange', verified: 'teal', cancelled: 'gray' };

export function RequestStatusBadge({ status, ...props }: { status: RequestStatus } & BadgeProps) {
  return (
    <Badge color={REQUEST_COLORS[status]} {...props}>
      {REQUEST_STATUS_LABELS[status]}
    </Badge>
  );
}

export function BdStatusBadge({ status, ...props }: { status: BdReviewStatus } & BadgeProps) {
  return (
    <Badge color={BD_COLORS[status]} variant={status === 'not_sent' ? 'outline' : 'light'} {...props}>
      {BD_REVIEW_STATUS_LABELS[status]}
    </Badge>
  );
}

export function TaskStatusBadge({ status, ...props }: { status: WorkItemStatus } & BadgeProps) {
  return (
    <Badge color={ITEM_COLORS[status]} {...props}>
      {WORK_ITEM_STATUS_LABELS[status]}
    </Badge>
  );
}

/** Target date with an overdue warning when the task is still open and the date has passed. */
export function DueDate({ date, open = true, compact = false }: { date: string | null; open?: boolean; compact?: boolean }) {
  if (!date) {
    return (
      <Text fz="sm" c="dimmed">
        {compact ? '—' : 'No date yet'}
      </Text>
    );
  }
  const overdue = open && date < businessToday();
  const content = (
    <Group gap={4} wrap="nowrap">
      {overdue ? <IconAlertTriangle size={14} color="var(--mantine-color-orange-6)" /> : <IconCalendar size={14} color="var(--mantine-color-dimmed)" />}
      <Text fz="sm" c={overdue ? 'orange.7' : undefined} fw={overdue ? 600 : undefined} className="nowrap">
        {formatBusinessDate(date)}
      </Text>
    </Group>
  );
  return overdue ? <Tooltip label="Overdue">{content}</Tooltip> : content;
}
